import { useMemo } from 'react';
import { toast } from 'sonner';
import { useEditor } from '../context/EditorContext';
import { panelLayout, fitToPanel } from '../lib/panelFit';
import { DEFAULT_PANEL_GUTTER, gutterLabel } from '../lib/foldPresets';

/**
 * Fit to Panel: size the selected object(s) to the brochure panel each sits in — between two fold lines (or a fold line and the
 * page edge) minus the panel margins, and between the page margins across the folds. `widthOnly` keeps the extent across the
 * folds and fits only the panel width.
 */
export const usePanelFit = () => {
  const { state, dispatch, getCurrentPage, getActiveObjects, pxToUnit } = useEditor();
  const page = getCurrentPage();
  const layout = useMemo(() => panelLayout(state.guides, page), [state.guides, page]);
  const unit = state.canvasUnit || 'in';

  const fit = (ids, { widthOnly = false } = {}) => {
    if (!layout) {
      toast.info('No fold lines on this page', { description: 'Add them with Page Design ▸ Guides ▸ Fold Lines, or right-click a ruler guide and choose Fold line.' });
      return false;
    }
    const objects = getActiveObjects();
    const results = (ids || [])
      .map((id) => objects.find((o) => o.id === id))
      .filter((o) => o && o.type !== 'group')
      .map((o) => ({ id: o.id, ...fitToPanel(layout, o, { widthOnly }) }))
      .filter((r) => r.data);
    if (!results.length) return false;
    dispatch({ type: 'UPDATE_OBJECTS', payload: results.map(({ id, data }) => ({ id, data })) });
    const first = results[0];
    const w = first.data.width ?? objects.find((o) => o.id === first.id)?.width;
    const h = first.data.height ?? objects.find((o) => o.id === first.id)?.height;
    const where = results.length === 1 ? `panel ${first.panel + 1} of ${first.count}` : `${results.length} objects fitted to their panels`;
    toast.success(results.length === 1 ? `Fitted to ${where}` : where, {
      description: results.length === 1 ? `${widthOnly ? 'Width' : 'Size'} ${pxToUnit(w)} × ${pxToUnit(h)} ${unit}${layout.margins.fromGuides ? '' : ' · no margin guides, so ¼ in was kept from the page edge'}` : undefined,
    });
    return true;
  };

  /**
   * Panel columns: fit the text box across its panel (keeping top and height for side-by-side panels) and split it into
   * `n` columns whose gutter is the panel margin of the fold lines, so column gaps match the space kept from the crease.
   */
  const fitColumns = (ids, n) => {
    if (!layout) {
      toast.info('No fold lines on this page', { description: 'Add them with Page Design ▸ Guides ▸ Fold Lines first.' });
      return false;
    }
    const objects = getActiveObjects();
    const results = (ids || [])
      .map((id) => objects.find((o) => o.id === id))
      .filter((o) => o && (o.type === 'textbox' || o.type === 'text'))
      .map((o) => ({ id: o.id, o, ...fitToPanel(layout, o, { widthOnly: layout.orientation === 'vertical' }) }))
      .filter((r) => r.data);
    if (!results.length) { toast.info('Select a text box to split into panel columns.'); return false; }
    const cols = Math.max(1, Math.min(3, Math.round(n)));
    dispatch({
      type: 'UPDATE_OBJECTS',
      payload: results.map(({ id, o, data, gutter }) => ({ id, data: { ...data, properties: { ...(o.properties || {}), columns: cols, columnGap: gutter || DEFAULT_PANEL_GUTTER } } })),
    });
    const g = results[0].gutter || DEFAULT_PANEL_GUTTER;
    toast.success(`${cols} panel columns${results.length > 1 ? ` on ${results.length} text boxes` : ''}`, { description: `Fitted to panel ${results[0].panel + 1} of ${results[0].count} · column gutter ${gutterLabel(g)} (the panel margin)` });
    return true;
  };

  // Label for the partial fit: side-by-side panels (vertical folds) fit the width, stacked panels (horizontal folds) the height
  const partial = layout?.orientation === 'horizontal'
    ? { label: 'Fit Height to Panel', hint: 'keep left and width' }
    : { label: 'Fit Width to Panel', hint: 'keep top and height' };

  return { layout, canFit: !!layout, fit, fitColumns, partial };
};
